import { Rule, RuleType, TableTotalsSpec } from "../../components/interfaces/Rule";
import {
  TableCellSnapshot,
  TableTotalsResultModel,
  TableSnapshotGrid,
} from "../../components/rules/tableTotals/tableTotalsResult.types";
import { Tag } from "../../components/interfaces/Tag";
import { CoObjectSummary } from "../../components/interfaces/CoObject";
import { buildCoObjectSummaries } from "./ContentControlHelper";
import { RunResult, RunStatusType } from "../../components/rules/RulesResultPanel";
import { BatchResultsResponse, BatchScriptItem, ScriptResultItem } from "../../api/models/script-runner";
import { extractTagIdsFromScript } from "./TagsHelper";
import { tokenizeScriptForRendering } from "../grammar/RuleGrammarParser";
import * as uuid from "uuid";
import { executeBatch, joinBatch } from "../../api/scriptRunner/ScriptRunner";
import { getDocumentPartMetadataFromCustomXml } from "./CustomXmlHelper";
import { DocumentPartMetadata } from "../../api/models/document-part-metadata";
import {
  MissingObjectsException,
  MissingTagException,
  SingleObjectException,
} from "../exception/RulesRunnerExceptions";
import { renderConsistencyContent, renderScriptWithTagPills, renderScriptWithValue } from "./RulesHelper";
import { formatMessage, formatMessageWithParamsDefault } from "./LanguageHelper";
import { FormatMetadata } from "../../api/models/format-metadata";
import { builtInNumberFormat } from "../constants/formats";
import { readTableSnapshots } from "../../office/ContentControl";
import { selectValidationCoObjects } from "../../redux/feature/validation/validation-slice";
import { store } from "../../redux/store/store";
import {
  CAPTION_TT_RESULT_STRUCTURE_CHANGED,
  CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED,
  TABLE_TOTALS_CAPTION_DEFAULTS,
} from "../../components/document/validation/validationCaptions";
// Re-exported from runAggregation.ts as a value import — safe despite the circular type-only
// imports between these two files, since type imports are erased at compile time.
import { EXECUTION_ABORTED_EXCEPTION_CODE } from "./runAggregation";

export type ConsistencyTagErrorKind = "MISSING_TAG" | "MISSING_OBJECTS";

type ConsistencyTagResult = {
  status: RunStatusType;
  label: string;
  objects: CoObjectSummary[];
  failureMessage?: string;
  errorMessage?: string;
  errorKind?: ConsistencyTagErrorKind;
};

const CONSISTENCY_RULE_PREFIX = `${RuleType.CONSISTENCY}_`;
const CUSTOM_RULE_PREFIX = `${RuleType.CUSTOM}_`;
export const TABLE_TOTALS_RULE_PREFIX = `${RuleType.TABLE_TOTALS}_`;
const PARSE_ERROR_CODE = 68; // 0x44 — scriptRunner PARSE_ERROR (non-numeric / unparseable cell)

export const runRules = async (
  selectedRuleIds: string[],
  allRules: Rule[],
  allTags: Tag[],
  onRuleExecuted?: (res: RunResult[]) => void
): Promise<{ results: RunResult[] }> => {
  console.debug("[RulesRunner] Filtering selected rules from ids:", selectedRuleIds);
  const selectedRules = selectedRuleIds.map((id) => allRules.find((r) => r.id === id)).filter(Boolean);
  if (!selectedRules.length) {
    return { results: [] };
  }

  console.debug("[RulesRunner] Building ruleId → tags map");
  const ruleIdToTags = getRuleIdToTags(selectedRules, allTags);

  console.debug("[RulesRunner] Fetching objects for tags");
  const tagIdToObjects = await getTagIdToObjects([...ruleIdToTags.values()].flat());

  console.debug("[RulesRunner] Executing batch");
  const { batchResults, errorKindMap, tableTotalsResultMap } = await executeRules(
    selectedRules,
    ruleIdToTags,
    tagIdToObjects
  );

  console.debug("[RulesRunner] Building display results from batch response");
  const results: RunResult[] = buildResult(
    batchResults,
    selectedRules,
    ruleIdToTags,
    tagIdToObjects,
    errorKindMap,
    tableTotalsResultMap
  );

  onRuleExecuted?.(results);
  return { results };
};

// Exported so the streaming controller (`RunController.ts`) can reuse the exact same ruleId ->
// tags resolution instead of re-deriving it.
export const getRuleIdToTags = (rules: Rule[], allTags: Tag[]): Map<string, Tag[]> => {
  const ruleIdToTag = new Map<string, Tag[]>();
  for (const rule of rules) {
    let tags: Tag[];
    switch (rule.type) {
      case RuleType.CONSISTENCY:
        tags = (rule.tagIds || []).map((id) => allTags.find((t) => t.id === id)).filter((t): t is Tag => Boolean(t));
        break;
      case RuleType.TABLE_TOTALS:
        // TABLE_TOTALS rules don't use tags — handled separately in executeRules.
        tags = [];
        break;
      case RuleType.CUSTOM:
      default: {
        const scriptTagIds = extractTagIdsFromScript(rule.script || "");
        tags = scriptTagIds.map((id) => {
          const found = allTags.find((t) => t.id === id);
          if (found) return found;
          return { id, label: id, isDeleted: true, objects: [] };
        });
        break;
      }
    }
    ruleIdToTag.set(rule.id, tags);
  }
  return ruleIdToTag;
};

// Exported for the same reason as `getRuleIdToTags` — reused verbatim by the streaming controller.
export const getTagIdToObjects = async (tags: Tag[]): Promise<Map<string, CoObjectSummary[]>> => {
  const uniqueTags = [...new Map(tags.map((t) => [t.id, t])).values()];

  console.debug("[getTagIdToObjects] Collecting unique object IDs");
  const allObjectIds = [...new Set(uniqueTags.flatMap((t) => (t.objects ?? []).map((o) => o.id).filter(Boolean)))];

  console.debug("[getTagIdToObjects] Fetching summaries for", allObjectIds.length, "objects");
  const summaryById = allObjectIds.length ? await buildCoObjectSummaries(allObjectIds) : {};

  console.debug("[getTagIdToObjects] Enriching tag-to-objects map with text");
  const tagIdToObjectSummary = new Map<string, CoObjectSummary[]>();
  uniqueTags.forEach((tag) => {
    const thisTagObjectSummaries = (tag.objects ?? []).map((tagObj) => summaryById[tagObj.id] ?? tagObj);
    tagIdToObjectSummary.set(tag.id, thisTagObjectSummaries);
  });

  return tagIdToObjectSummary;
};

function mapExceptionToErrorKind(error: unknown): ConsistencyTagErrorKind | undefined {
  if (error instanceof MissingTagException) return "MISSING_TAG";
  if (error instanceof MissingObjectsException) return "MISSING_OBJECTS";
  return undefined;
}

const buildConsistencyTagScripts = (
  ruleId: string,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata
): {
  scripts: BatchScriptItem[];
  earlyResultItems: ScriptResultItem[];
  errorKindMap: Map<string, ConsistencyTagErrorKind>;
} => {
  const scripts: BatchScriptItem[] = [];
  const earlyResultItems: ScriptResultItem[] = [];
  const errorKindMap = new Map<string, ConsistencyTagErrorKind>();

  // Dedup by tag id: a self-referential rule (e.g. `{{tag1}} > {{tag1}}`) can repeat a tag, and two
  // scripts sharing the same scriptId would make the scriptrunner batch reject with HTTP 400.
  const uniqueRuleTags = [...new Map(ruleTags.map((t) => [t.id, t])).values()];

  for (const ruleTag of uniqueRuleTags) {
    try {
      scripts.push({
        source: buildConsistencyScriptItem(ruleId, ruleTag, tagIdToObjects, documentMetadata),
      });
    } catch (e) {
      if (e instanceof SingleObjectException) {
        earlyResultItems.push({ status: "SUCCESS", scriptId: buildScriptId(RuleType.CONSISTENCY, ruleId, ruleTag.id) });
      } else {
        const errorKind = mapExceptionToErrorKind(e);
        if (errorKind !== undefined) {
          const scriptId = buildScriptId(RuleType.CONSISTENCY, ruleId, ruleTag.id);
          errorKindMap.set(scriptId, errorKind);
          earlyResultItems.push({
            status: "ERROR",
            errorMessage: (e as Error).message,
            scriptId,
          });
        } else {
          console.error(e);
        }
      }
    }
  }

  return { scripts, earlyResultItems, errorKindMap };
};

const buildCustomRuleExtraScript = (
  rule: Rule,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata
): { script?: BatchScriptItem; earlyResult?: ScriptResultItem } => {
  try {
    const customScript = buildScriptItem(rule, ruleTags, tagIdToObjects, documentMetadata);
    return customScript ? { script: { source: customScript } } : {};
  } catch (e) {
    if (e instanceof MissingTagException || e instanceof MissingObjectsException) {
      console.log("early result", e.message);
      return {
        earlyResult: { status: "ERROR", errorMessage: e.message, scriptId: buildScriptId(RuleType.CUSTOM, rule.id) },
      };
    }
    console.error(e);
    return {};
  }
};

// Exported so the streaming controller can recompute `errorKindMap` (not exposed by
// `buildUnifiedRunBatch`) via this same pure/side-effect-free builder, without forking its logic.
export const buildBatchScriptItems = (
  selectedRules: Rule[],
  ruleIdToTags: Map<string, Tag[]>,
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata
): {
  batchScripts: BatchScriptItem[];
  earlyResults: ScriptResultItem[];
  errorKindMap: Map<string, ConsistencyTagErrorKind>;
} => {
  const earlyResults: ScriptResultItem[] = [];
  const batchScripts: BatchScriptItem[] = [];
  const errorKindMap = new Map<string, ConsistencyTagErrorKind>();

  for (const rule of selectedRules) {
    const ruleTags = ruleIdToTags.get(rule.id) ?? [];
    switch (rule.type) {
      case RuleType.CONSISTENCY: {
        const {
          scripts,
          earlyResultItems,
          errorKindMap: ruleErrorKindMap,
        } = buildConsistencyTagScripts(rule.id, ruleTags, tagIdToObjects, documentMetadata);
        batchScripts.push(...scripts);
        earlyResults.push(...earlyResultItems);
        ruleErrorKindMap.forEach((kind, key) => errorKindMap.set(key, kind));
        break;
      }
      case RuleType.TABLE_TOTALS:
        // TABLE_TOTALS rules are excluded — their batch is built separately in executeRules.
        break;
      case RuleType.CUSTOM:
      default: {
        const {
          scripts,
          earlyResultItems,
          errorKindMap: ruleErrorKindMap,
        } = buildConsistencyTagScripts(rule.id, ruleTags, tagIdToObjects, documentMetadata);

        batchScripts.push(...scripts);
        earlyResults.push(...earlyResultItems);
        ruleErrorKindMap.forEach((kind, key) => errorKindMap.set(key, kind));

        const hasConsistencyFailures = earlyResultItems.some((r) => r.status === "ERROR");

        if (!hasConsistencyFailures) {
          const { script, earlyResult } = buildCustomRuleExtraScript(rule, ruleTags, tagIdToObjects, documentMetadata);
          if (script) batchScripts.push(script);
          if (earlyResult) earlyResults.push(earlyResult);
        }
        break;
      }
    }
  }

  return { batchScripts, earlyResults, errorKindMap };
};

const buildBatchContext = (tagIdToObjects: Map<string, CoObjectSummary[]>): Record<string, string> => {
  const context: Record<string, string> = {};
  for (const objects of tagIdToObjects.values()) {
    for (const obj of objects) {
      if (obj.text != null) {
        context[toContextKey(obj.id)] ??= obj.text;
      }
    }
  }
  return context;
};

// Exported so the multi-batch pool can slice the unified `scripts` array into per-chunk subsets
// using the same `@scriptId` directive parsing, instead of re-deriving it.
export const extractScriptIdFromSource = (source: string | undefined): string | undefined => {
  if (!source) return undefined;
  const firstLine = source.split("\n")[0].trim();
  const prefix = "@scriptId ";
  if (!firstLine.startsWith(prefix)) return undefined;
  return firstLine.slice(prefix.length).trim() || undefined;
};

// TABLE_TOTALS run-time helpers

/**
 * Builds a minimal ERROR RunResult for a TABLE_TOTALS rule — used when the rule is malformed, the
 * content control/table is missing, or the scriptRunner returns an un-evaluable ERROR status.
 * @param rule - The TABLE_TOTALS rule.
 * @param now - ISO timestamp for the result's `id` suffix and `info.timestamp`.
 * @param reason - Discriminates the error cause, driving both `errorModel.errorReason` and the
 *   top-level `info.message`.
 */
export const buildTableTotalsErrorResult = (
  rule: Rule,
  now: string,
  reason: "STRUCTURE_CHANGED" | "NON_NUMERIC_VALUE" = "STRUCTURE_CHANGED"
): RunResult => {
  const errorModel: TableTotalsResultModel = {
    status: "ERROR",
    errorReason: reason,
    snapshot: [],
    rowCount: 0,
    columnCount: 0,
    addends: [],
  };
  const message =
    reason === "NON_NUMERIC_VALUE"
      ? formatMessageWithParamsDefault(
          CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED,
          TABLE_TOTALS_CAPTION_DEFAULTS[CAPTION_TT_RESULT_NUMERIC_VALUE_EXPECTED],
          {}
        )
      : formatMessage(CAPTION_TT_RESULT_STRUCTURE_CHANGED);
  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: "TABLE_TOTALS",
    info: {
      status: "ERROR",
      timestamp: now,
      message,
      tableTotals: errorModel,
    },
  };
};

/**
 * Builds a CANCELED RunResult for a TABLE_TOTALS rule whose script was aborted. The nested
 * `tableTotals.status` reuses the "ERROR" shape only to satisfy the type (which has no CANCELED
 * slot) — it's never rendered, since a CANCELED row is never expandable. The outer
 * `info.status: "CANCELED"` is what the results panel actually reads.
 */
const buildTableTotalsCanceledResult = (rule: Rule, now: string): RunResult => {
  const cancelModel: TableTotalsResultModel = {
    status: "ERROR",
    snapshot: [],
    rowCount: 0,
    columnCount: 0,
    addends: [],
  };
  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: "TABLE_TOTALS",
    info: {
      status: "CANCELED",
      timestamp: now,
      tableTotals: cancelModel,
    },
  };
};

/**
 * Builds a VERIFIED or FAILED RunResult for a TABLE_TOTALS rule from the run-time snapshot grid and
 * scriptRunner result. Delegates to `buildTableTotalsErrorResult`/`buildTableTotalsCanceledResult`
 * for ERROR/CANCELED status.
 */
export const buildTableTotalsResult = (
  rule: Rule,
  spec: NonNullable<Rule["tableTotals"]>,
  grid: TableSnapshotGrid,
  scriptResult: ScriptResultItem,
  now: string,
  tableName?: string
): RunResult => {
  const status = evaluateStatus([scriptResult]);

  if (status === "ERROR") {
    const reason = scriptResult.exceptionCode === PARSE_ERROR_CODE ? "NON_NUMERIC_VALUE" : "STRUCTURE_CHANGED";
    return buildTableTotalsErrorResult(rule, now, reason);
  }

  if (status === "CANCELED") {
    return buildTableTotalsCanceledResult(rule, now);
  }

  const { rowCount, columnCount } = grid;

  // Full snapshot grid: grid.grid is logical and rectangular (rowCount × columnCount, with
  // columnCount tblGrid-derived), so every entry here is within-bounds by construction. Span
  // metadata (colSpan/rowSpan/covered) is copied through from grid.spans so the dialog payload
  // (T6) can render merged cells and grey out covered positions instead of showing them as data.
  const snapshot: TableCellSnapshot[][] = grid.grid.map((rowArr, r) =>
    rowArr.map((value, c) => {
      const span = grid.spans?.[r]?.[c];
      return {
        row: r,
        column: c,
        value,
        outOfBounds: false,
        format: grid.formats?.[r]?.[c],
        colSpan: span?.colSpan,
        rowSpan: span?.rowSpan,
        covered: span?.covered,
        unclaimed: span?.unclaimed,
      };
    })
  );

  // Addend snapshots — mark out-of-bounds when ref falls outside current table shape
  const addends: TableCellSnapshot[] = spec.addends.map((ref) => {
    const outOfBounds = ref.row >= rowCount || ref.column >= columnCount;
    const value = outOfBounds ? "" : (grid.grid[ref.row]?.[ref.column] ?? "");
    return { row: ref.row, column: ref.column, value, outOfBounds };
  });

  // Subtotal snapshot
  const subtotalRef = spec.subtotal;
  const subtotalOutOfBounds = subtotalRef.row >= rowCount || subtotalRef.column >= columnCount;
  const subtotalValue = subtotalOutOfBounds ? "" : (grid.grid[subtotalRef.row]?.[subtotalRef.column] ?? "");
  const subtotal: TableCellSnapshot = {
    row: subtotalRef.row,
    column: subtotalRef.column,
    value: subtotalValue,
    outOfBounds: subtotalOutOfBounds,
  };

  let computedSum: string | undefined;
  let expectedSubtotal: string | undefined;
  const displayExpression = buildTableTotalsDisplayExpression(addends, subtotal);

  if (status === "VERIFIED") {
    // Both sides are equal: report the subtotal snapshot value for both fields (DD-E)
    computedSum = subtotalValue;
    expectedSubtotal = subtotalValue;
  } else {
    // FAILED: parse FORMAT output "sum=<X>\nsubtotal=<Y>" from failureMessage
    const parts = (scriptResult.failureMessage ?? "").split("\n");
    computedSum = parts[0]?.replace("sum=", "") ?? "";
    expectedSubtotal = parts[1]?.replace("subtotal=", "") ?? "";
  }

  const model: TableTotalsResultModel = {
    status,
    snapshot,
    rowCount,
    columnCount,
    addends,
    subtotal,
    computedSum,
    expectedSubtotal,
    displayExpression,
    tableName: tableName ?? "",
  };

  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: "TABLE_TOTALS",
    info: {
      status,
      timestamp: now,
      tableTotals: model,
    },
  };
};

// Deduped TABLE_TOTALS snapshot shape shared between `resolveValidTableTotalsRules` (produces it)
// and `buildUnifiedRunBatch`/the streaming controller (consume it).
export type ValidTableTotalsRule = {
  rule: Rule;
  grid: TableSnapshotGrid;
  spec: NonNullable<Rule["tableTotals"]>;
  tableName: string;
  tableFormatMetadata?: FormatMetadata;
};

/**
 * A TABLE_TOTALS rule that resolved a contentControlId but hasn't had its table snapshot read yet
 * — collects distinct content-control ids before the single dedup `readTableSnapshots` call.
 */
type AddressableTableTotalsRule = {
  rule: Rule;
  spec: NonNullable<Rule["tableTotals"]>;
  tableName: string;
  tableFormatMetadata?: FormatMetadata;
  contentControlId: string;
};

// Exported so the streaming controller can reuse the same deduped snapshot read as
// `executeTableTotalsRules` below, instead of duplicating the dedup logic.
export const resolveValidTableTotalsRules = async (
  tableTotalsRules: Rule[],
  coObjects: ReturnType<typeof selectValidationCoObjects>,
  resultMap: Map<string, RunResult>,
  now: string
): Promise<ValidTableTotalsRule[]> => {
  // Pass 1: resolve each rule's contentControlId, short-circuiting malformed/unresolved rules to
  // an immediate ERROR result. Doesn't touch the document yet.
  const addressableRules: AddressableTableTotalsRule[] = [];

  for (const rule of tableTotalsRules) {
    // Guard: malformed spec — missing tableTotals, empty addends, or undefined subtotal (DD-H, DD-I)
    if (!rule.tableTotals || rule.tableTotals.addends.length === 0 || !rule.tableTotals.subtotal) {
      console.debug("[executeTableTotalsRules] Malformed TABLE_TOTALS rule:", rule.id);
      resultMap.set(rule.id, buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED"));
      continue;
    }

    const spec = rule.tableTotals;

    const coObject = coObjects.find((o) => o.id === spec.tableId);
    const tableName = coObject?.info?.objectName ?? "";
    const tableFormatMetadata = coObject?.advanced?.formatMetadata;
    const contentControlId = coObject?.contentControlId;
    if (contentControlId === undefined) {
      console.debug("[executeTableTotalsRules] contentControlId not found for rule:", rule.id);
      resultMap.set(rule.id, buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED"));
      continue;
    }

    addressableRules.push({ rule, spec, tableName, tableFormatMetadata, contentControlId: String(contentControlId) });
  }

  if (addressableRules.length === 0) {
    return [];
  }

  // Dedup by content-control id: read each table exactly once via a single readTableSnapshots
  // call, regardless of how many rules reference it.
  const distinctIds = Array.from(new Set(addressableRules.map((r) => r.contentControlId)));
  const snapshots = await readTableSnapshots(distinctIds);

  // Pass 2: validate each rule's snapshot individually — a missing/out-of-bounds table only errors
  // that rule; every other rule stays unaffected.
  const validRules: ValidTableTotalsRule[] = [];

  for (const { rule, spec, tableName, tableFormatMetadata, contentControlId } of addressableRules) {
    const grid = snapshots.get(contentControlId) ?? { found: false, rowCount: 0, columnCount: 0, grid: [] };
    if (!grid.found) {
      console.debug("[executeTableTotalsRules] Table not found in document for rule:", rule.id);
      resultMap.set(rule.id, buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED"));
      continue;
    }

    // Structural bounds pre-check: grid.grid is logical and rectangular (rowCount × columnCount,
    // with columnCount tblGrid-derived), so an out-of-bounds cell reads back as "" and PARSE("")
    // resolves to 0 with no BE error, so a structural change (e.g. a removed row/column) would
    // otherwise silently look like a valid (and possibly VERIFIED) computation. Detect it here,
    // by coordinates, before the rule is ever sent to the scriptRunner batch.
    //
    // A ref landing on a position covered by another cell's colSpan/rowSpan is rejected for the
    // same reason: its "" value is a merge placeholder, not a legitimate zero, so summing it
    // would silently validate a phantom zero rather than surfacing the structural change.
    const { rowCount, columnCount } = grid;
    const isCovered = (ref: { row: number; column: number }) => grid.spans?.[ref.row]?.[ref.column]?.covered === true;
    const hasOutOfBoundsRef =
      spec.addends.some((ref) => ref.row >= rowCount || ref.column >= columnCount || isCovered(ref)) ||
      spec.subtotal.row >= rowCount ||
      spec.subtotal.column >= columnCount ||
      isCovered(spec.subtotal);
    if (hasOutOfBoundsRef) {
      console.debug("[executeTableTotalsRules] Out-of-bounds addend/subtotal ref for rule:", rule.id);
      resultMap.set(rule.id, buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED"));
      continue;
    }

    validRules.push({ rule, grid, spec, tableName, tableFormatMetadata });
  }

  return validRules;
};

/**
 * Extracts the addend + subtotal cells a TABLE_TOTALS rule's script/context need from its snapshot
 * grid. Shared by `buildTableTotalsBatchContext` and `buildUnifiedRunBatch`'s context derivation.
 */
const extractTableTotalsCells = (
  spec: NonNullable<Rule["tableTotals"]>,
  grid: TableSnapshotGrid
): Array<{ row: number; column: number; value: string }> => {
  const cells: Array<{ row: number; column: number; value: string }> = [];
  for (const ref of spec.addends) {
    cells.push({ row: ref.row, column: ref.column, value: grid.grid[ref.row]?.[ref.column] ?? "" });
  }
  const sub = spec.subtotal;
  cells.push({ row: sub.row, column: sub.column, value: grid.grid[sub.row]?.[sub.column] ?? "" });
  return cells;
};

const buildTableTotalsBatchContext = (validRules: ValidTableTotalsRule[]): Record<string, string> => {
  const batchContext: Record<string, string> = {};

  for (const { rule, grid, spec } of validRules) {
    const ruleContext = buildTableTotalsContext(rule.id, spec, extractTableTotalsCells(spec, grid));
    Object.assign(batchContext, ruleContext);
  }

  return batchContext;
};

/**
 * Resolves, snapshots, and evaluates all TABLE_TOTALS rules in one dedicated silent scriptRunner
 * batch. Returns a Map<ruleId, RunResult> for all rules, including ERROR ones.
 */
const executeTableTotalsRules = async (
  tableTotalsRules: Rule[],
  documentMetadata: DocumentPartMetadata
): Promise<Map<string, RunResult>> => {
  const now = new Date().toISOString();
  const resultMap = new Map<string, RunResult>();

  if (tableTotalsRules.length === 0) {
    return resultMap;
  }

  const coObjects = selectValidationCoObjects(store.getState());

  // Step 1: Resolve + snapshot each TABLE_TOTALS rule
  const validRules = await resolveValidTableTotalsRules(tableTotalsRules, coObjects, resultMap, now);

  if (validRules.length === 0) {
    return resultMap;
  }

  // Step 2: Build the dedicated silent batch for all valid TABLE_TOTALS rules
  const batchScripts: BatchScriptItem[] = validRules.map(({ rule, spec, tableFormatMetadata }) => ({
    source: buildTableTotalsScript(rule.id, spec, documentMetadata, tableFormatMetadata),
  }));

  const batchContext = buildTableTotalsBatchContext(validRules);

  const ttBatchId = uuid.v4();
  await executeBatch(ttBatchId, { context: batchContext, scripts: batchScripts }, { params: { notShowLoading: true } });
  const ttBatchResults = await joinBatch(ttBatchId, undefined, { params: { notShowLoading: true } });

  // Fix scriptIds via positional index (compile errors carry no scriptId)
  for (const result of ttBatchResults.results ?? []) {
    if (!result.scriptId && result.index != null) {
      result.scriptId = extractScriptIdFromSource(batchScripts[result.index]?.source);
    }
  }

  // Step 3: Build a RunResult for each valid rule from its scriptRunner result
  for (const { rule, grid, spec, tableName } of validRules) {
    const scriptId = buildScriptId(RuleType.TABLE_TOTALS, rule.id);
    const scriptResult = (ttBatchResults.results ?? []).find((r) => (r.scriptId ?? "") === scriptId);

    if (!scriptResult) {
      console.debug("[executeTableTotalsRules] No scriptResult found for rule:", rule.id);
      resultMap.set(rule.id, buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED"));
      continue;
    }

    resultMap.set(rule.id, buildTableTotalsResult(rule, spec, grid, scriptResult, now, tableName));
  }

  return resultMap;
};

const executeRules = async (
  selectedRules: Rule[],
  ruleIdToTags: Map<string, Tag[]>,
  tagIdToObjects: Map<string, CoObjectSummary[]>
) => {
  const documentMetadata = await getDocumentPartMetadataFromCustomXml();

  // Partition: TABLE_TOTALS rules run in a separate dedicated silent batch (T8);
  // CUSTOM/CONSISTENCY rules follow the existing path unchanged.
  const tableTotalsRules = selectedRules.filter((r) => r.type === RuleType.TABLE_TOTALS);
  const otherRules = selectedRules.filter((r) => r.type !== RuleType.TABLE_TOTALS);

  const { batchScripts, earlyResults, errorKindMap } = buildBatchScriptItems(
    otherRules,
    ruleIdToTags,
    tagIdToObjects,
    documentMetadata
  );

  let batchResults: BatchResultsResponse;

  if (batchScripts.length > 0) {
    const batchId = uuid.v4();
    await executeBatch(batchId, { context: buildBatchContext(tagIdToObjects), scripts: batchScripts });
    batchResults = await joinBatch(batchId);
    // Compile errors carry no scriptId; recover it via the positional index.
    for (const result of batchResults.results ?? []) {
      if (!result.scriptId && result.index != null) {
        result.scriptId = extractScriptIdFromSource(batchScripts[result.index]?.source);
      }
    }
  } else {
    batchResults = { batchId: uuid.v4(), results: [] };
  }

  batchResults.results = [...(batchResults.results ?? []), ...earlyResults];

  // Dedicated silent batch for TABLE_TOTALS rules (notShowLoading: true)
  const tableTotalsResultMap = await executeTableTotalsRules(tableTotalsRules, documentMetadata);

  return { batchResults, errorKindMap, tableTotalsResultMap };
};

// Exported so the streaming controller can call this per-row (with a one-element `selectedRules`
// and that row's own accumulated results) to build a terminal row via the same content-builders
// the blocking path uses, guaranteeing parity.
export const buildResult = (
  batchResults: BatchResultsResponse,
  selectedRules: Rule[],
  ruleIdToTags: Map<string, Tag[]>,
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  errorKindMap: Map<string, ConsistencyTagErrorKind>,
  tableTotalsResultMap: Map<string, RunResult>
): RunResult[] => {
  const now = new Date().toISOString();
  const scriptResults = batchResults?.results ?? [];

  return selectedRules.map((rule) => {
    const tags = ruleIdToTags.get(rule.id) ?? [];
    const consistencyPrefix = `${CONSISTENCY_RULE_PREFIX}${rule.id}`;
    const customPrefix = `${CUSTOM_RULE_PREFIX}${rule.id}`;

    console.debug("[buildResult] Separating consistency and custom results for rule:", rule.id);
    const consistencyResults = scriptResults.filter((r) => (r.scriptId ?? "").startsWith(consistencyPrefix));
    const customResults = scriptResults.filter((r) => (r.scriptId ?? "").startsWith(customPrefix));

    const ruleType = rule.type ?? RuleType.CONSISTENCY;
    console.debug("[buildResult] Rule type from batch:", ruleType, "for rule:", rule.id);

    switch (ruleType) {
      case RuleType.CUSTOM:
        return buildCustomResult(rule, tags, consistencyResults, customResults, tagIdToObjects, now, errorKindMap);
      case RuleType.TABLE_TOTALS: {
        // Results are pre-computed by executeTableTotalsRules (dedicated silent batch).
        const prebuilt = tableTotalsResultMap.get(rule.id);
        if (prebuilt) return prebuilt;
        // Fallback: should not occur in the normal flow
        console.warn("[buildResult] TABLE_TOTALS prebuilt result missing for rule:", rule.id);
        return buildTableTotalsErrorResult(rule, now, "STRUCTURE_CHANGED");
      }
      case RuleType.CONSISTENCY:
      default:
        return buildConsistencyResult(rule, tags, consistencyResults, tagIdToObjects, now, errorKindMap);
    }
  });
};

const buildScriptId = (ruleType: RuleType, ruleId: string, tagId?: string) => {
  switch (ruleType) {
    case RuleType.CONSISTENCY:
      return `${CONSISTENCY_RULE_PREFIX}${ruleId}_${tagId}`;
    case RuleType.CUSTOM:
      return `${CUSTOM_RULE_PREFIX}${ruleId}`;
    case RuleType.TABLE_TOTALS:
      return `${TABLE_TOTALS_RULE_PREFIX}${ruleId}`;
  }
};

// Unified single-batch builder (all rule types)

/**
 * ruleId -> every scriptId that row's scripts will (eventually) produce, matching the prefixes
 * `buildResult` correlates on: CONSISTENCY -> one `CONSISTENCY_<ruleId>_<tagId>` per tag; CUSTOM ->
 * its own consistency-precheck scriptIds plus `CUSTOM_<ruleId>` when a script was built;
 * TABLE_TOTALS -> exactly one `TABLE_TOTALS_<ruleId>`.
 *
 * This is the expected-set the aggregation contract needs so a row completes only once every
 * constituent scriptId has resolved.
 */
export type ExpectedScriptIdsByRule = Map<string, string[]>;

/** Return shape of `buildUnifiedRunBatch` — see its doc comment below. */
export interface UnifiedRunBatch {
  /** Every selected rule's scripts, flattened into ONE array. Never contains a duplicate scriptId. */
  scripts: BatchScriptItem[];
  /** ONE merged context: `CELL_<ruleId>_r_c` (table-totals) + `OBJ_<id>` (consistency/custom). */
  context: Record<string, any>;
  /** ruleId -> every scriptId that row expects to resolve (streamed or early). */
  expectedScriptIds: ExpectedScriptIdsByRule;
  /** Client-side synthesized ("early") results — resolved before the batch, never sent to it. */
  earlyResults: ScriptResultItem[];
  /** ruleId -> the scripts that rule contributes to `scripts` (empty array for a fully-early rule).
   * Lets the multi-batch packer read each rule's script count directly. */
  scriptsByRuleId: Map<string, BatchScriptItem[]>;
  /** ruleId -> the context keys that rule needs out of `context` (`OBJ_<id>` for
   * CONSISTENCY/CUSTOM, shared across rules referencing the same tag; `CELL_<ruleId>_*` for
   * TABLE_TOTALS, namespaced per rule). Lets the multi-batch packer build each chunk's own
   * per-chunk context. */
  contextKeysByRuleId: Map<string, string[]>;
}

/**
 * Assembles ONE unified `{scripts, context}` batch spanning every rule type (CONSISTENCY, CUSTOM,
 * TABLE_TOTALS), given the already-resolved tag/object data and already-snapshotted TABLE_TOTALS
 * rows. Also returns the `ruleId -> scriptId[]` expected-set the aggregation contract needs to seed
 * each row's initial state, plus any early results. Pure/synchronous — no I/O; only assembles data
 * via the same builders `buildBatchScriptItems`/`executeTableTotalsRules` already use.
 *
 * Duplicate-scriptId guard: if the same scriptId would be pushed twice (e.g. a duplicate rule id in
 * `selectedRules`), the second occurrence is dropped — first-wins, logged via `console.error`.
 *
 * Exported so the streaming controller can execute this same assembly as a single
 * `executeBatch`/`streamBatch` call, and so the multi-batch packer can chunk `scripts`/`context` by
 * rule/script-count/context-key union via `scriptsByRuleId`/`contextKeysByRuleId`.
 *
 * The blocking `runRules` path keeps its own separate dual-batch mechanics (a CUSTOM/CONSISTENCY
 * batch + a silent TABLE_TOTALS batch) unchanged — merging them would also merge their
 * `notShowLoading` UX, an observable behavior change outside this task's scope.
 *
 * @param selectedRules         - ALL selected rules (every RuleType), in their original order.
 * @param ruleIdToTags          - Per-rule tags (CONSISTENCY: rule.tagIds resolved; CUSTOM: tags
 *                                extracted from the script; TABLE_TOTALS: always `[]`). See
 *                                `getRuleIdToTags`.
 * @param tagIdToObjects        - Resolved objects per tag id. See `getTagIdToObjects`.
 * @param documentMetadata      - Document format metadata for locale-aware PARSE expressions.
 * @param validTableTotalsRules - The TABLE_TOTALS rows that survived `resolveValidTableTotalsRules`
 *                                (T8's single deduped `readTableSnapshots` read already applied). A
 *                                TABLE_TOTALS rule present in `selectedRules` but absent here already
 *                                failed one of that function's guards (malformed spec / missing
 *                                table / out-of-bounds ref) and gets a synthetic early ERROR result
 *                                instead of an unresolvable "expected" scriptId.
 */
/** Shape of `buildUnifiedRunBatch`'s local `pushScript` closure — see that function's doc comment. */
type PushScript = (item: BatchScriptItem) => { scriptId?: string; added: boolean };

/** Records ONE `pushScript` outcome into the running per-rule `scriptIds`/`scripts` accumulators —
 * shared by `collectTagPrecheckScripts`/`collectCustomExtraScript` below so this 2-line pattern
 * (used at every `pushScript` call site) exists in exactly one place. */
const recordPushedScript = (
  pushed: { scriptId?: string; added: boolean },
  scriptIds: string[],
  scripts: BatchScriptItem[],
  item: BatchScriptItem
): void => {
  if (pushed.scriptId) scriptIds.push(pushed.scriptId);
  if (pushed.added) scripts.push(item);
};

/**
 * Builds and pushes ONE rule's tag-consistency pre-check scripts (shared by CONSISTENCY rules and
 * CUSTOM rules that reference tags), routing every one through the CALLER's `pushScript` (so the
 * duplicate-scriptId guard applies uniformly across every rule). Mutates `earlyResults` in place
 * (mirrors every other accumulator-parameter in this file, e.g. `resolveValidTableTotalsRules`'s
 * `resultMap`). Also returns `earlyResultItems` so the caller can derive
 * `hasConsistencyFailures` without re-deriving the tag pre-check itself.
 */
const collectTagPrecheckScripts = (
  ruleId: string,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata,
  pushScript: PushScript,
  earlyResults: ScriptResultItem[]
): { scriptIds: string[]; scripts: BatchScriptItem[]; earlyResultItems: ScriptResultItem[] } => {
  const scriptIds: string[] = [];
  const scripts: BatchScriptItem[] = [];
  const { scripts: tagScripts, earlyResultItems } = buildConsistencyTagScripts(
    ruleId,
    ruleTags,
    tagIdToObjects,
    documentMetadata
  );

  for (const item of tagScripts) {
    recordPushedScript(pushScript(item), scriptIds, scripts, item);
  }
  for (const early of earlyResultItems) {
    earlyResults.push(early);
    if (early.scriptId) scriptIds.push(early.scriptId);
  }

  return { scriptIds, scripts, earlyResultItems };
};

/**
 * Builds and pushes a CUSTOM rule's OWN extra script (the rule's `rule.script` expression itself,
 * as opposed to its tag-consistency pre-checks above) — only ever called once the caller has already
 * confirmed the rule's tag pre-checks did not fail. Same `pushScript`/`earlyResults` contract as
 * `collectTagPrecheckScripts`.
 */
const collectCustomExtraScript = (
  rule: Rule,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata,
  pushScript: PushScript,
  earlyResults: ScriptResultItem[]
): { scriptIds: string[]; scripts: BatchScriptItem[] } => {
  const scriptIds: string[] = [];
  const scripts: BatchScriptItem[] = [];

  const { script, earlyResult } = buildCustomRuleExtraScript(rule, ruleTags, tagIdToObjects, documentMetadata);
  if (script) recordPushedScript(pushScript(script), scriptIds, scripts, script);
  if (earlyResult) {
    earlyResults.push(earlyResult);
    if (earlyResult.scriptId) scriptIds.push(earlyResult.scriptId);
  }

  return { scriptIds, scripts };
};

/**
 * Processes one CONSISTENCY/CUSTOM rule's contribution to the unified batch: its tag pre-check
 * scripts, plus — for a CUSTOM rule whose pre-checks all passed — its own extra script.
 */
const collectConsistencyOrCustomRuleScripts = (
  rule: Rule,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata,
  pushScript: PushScript,
  earlyResults: ScriptResultItem[]
): { scriptIds: string[]; scripts: BatchScriptItem[] } => {
  const tagResult = collectTagPrecheckScripts(
    rule.id,
    ruleTags,
    tagIdToObjects,
    documentMetadata,
    pushScript,
    earlyResults
  );

  const hasConsistencyFailures = tagResult.earlyResultItems.some((r) => r.status === "ERROR");
  if (rule.type !== RuleType.CUSTOM || hasConsistencyFailures) {
    return { scriptIds: tagResult.scriptIds, scripts: tagResult.scripts };
  }

  const customResult = collectCustomExtraScript(
    rule,
    ruleTags,
    tagIdToObjects,
    documentMetadata,
    pushScript,
    earlyResults
  );
  return {
    scriptIds: [...tagResult.scriptIds, ...customResult.scriptIds],
    scripts: [...tagResult.scripts, ...customResult.scripts],
  };
};

export const buildUnifiedRunBatch = (
  selectedRules: Rule[],
  ruleIdToTags: Map<string, Tag[]>,
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata,
  validTableTotalsRules: ValidTableTotalsRule[]
): UnifiedRunBatch => {
  const scripts: BatchScriptItem[] = [];
  const earlyResults: ScriptResultItem[] = [];
  const expectedScriptIds: ExpectedScriptIdsByRule = new Map();
  const scriptsByRuleId: Map<string, BatchScriptItem[]> = new Map();
  const contextKeysByRuleId: Map<string, string[]> = new Map();
  const seenScriptIds = new Set<string>();

  // Pushes one script unless its scriptId was already seen (keeps the first occurrence, drops and
  // logs later ones). `added` tells the caller whether this call appended `item`, so
  // `scriptsByRuleId` attributes each script to the one rule that first pushed it.
  const pushScript: PushScript = (item) => {
    const scriptId = extractScriptIdFromSource(item.source);
    if (scriptId) {
      if (seenScriptIds.has(scriptId)) {
        console.error("[buildUnifiedRunBatch] Duplicate scriptId dropped from unified batch:", scriptId);
        return { scriptId, added: false };
      }
      seenScriptIds.add(scriptId);
    }
    scripts.push(item);
    return { scriptId, added: true };
  };

  // CONSISTENCY + CUSTOM: also tracks, per rule, its own scripts (`scriptsByRuleId`) and context
  // keys (`contextKeysByRuleId`) as they're generated.
  for (const rule of selectedRules) {
    if (rule.type === RuleType.TABLE_TOTALS) continue; // handled below via validTableTotalsRules

    const ruleTags = ruleIdToTags.get(rule.id) ?? [];
    const { scriptIds, scripts: ruleScripts } = collectConsistencyOrCustomRuleScripts(
      rule,
      ruleTags,
      tagIdToObjects,
      documentMetadata,
      pushScript,
      earlyResults
    );

    expectedScriptIds.set(rule.id, scriptIds);
    scriptsByRuleId.set(rule.id, ruleScripts);
    // Context keys this rule needs (union, not sum, across its tags).
    contextKeysByRuleId.set(rule.id, buildRuleContextKeys(ruleTags, tagIdToObjects));
  }

  // TABLE_TOTALS: exactly one scriptId per rule; `CELL_<ruleId>_*` context keys are namespaced per
  // rule, never shared.
  for (const { rule, grid, spec, tableFormatMetadata } of validTableTotalsRules) {
    const scriptId = buildScriptId(RuleType.TABLE_TOTALS, rule.id);
    const item = { source: buildTableTotalsScript(rule.id, spec, documentMetadata, tableFormatMetadata) };
    const { added } = pushScript(item);
    expectedScriptIds.set(rule.id, [scriptId]);
    scriptsByRuleId.set(rule.id, added ? [item] : []);
    contextKeysByRuleId.set(
      rule.id,
      Object.keys(buildTableTotalsContext(rule.id, spec, extractTableTotalsCells(spec, grid)))
    );
  }

  // A TABLE_TOTALS rule absent from validTableTotalsRules already failed a guard there and will
  // never produce a batch script — treat it as fully early (synthetic ERROR).
  const validTableTotalsRuleIds = new Set(validTableTotalsRules.map(({ rule }) => rule.id));
  for (const rule of selectedRules) {
    if (rule.type !== RuleType.TABLE_TOTALS || validTableTotalsRuleIds.has(rule.id)) continue;
    const scriptId = buildScriptId(RuleType.TABLE_TOTALS, rule.id);
    earlyResults.push({ status: "ERROR", scriptId, errorMessage: formatMessage(CAPTION_TT_RESULT_STRUCTURE_CHANGED) });
    expectedScriptIds.set(rule.id, [scriptId]);
    scriptsByRuleId.set(rule.id, []);
    contextKeysByRuleId.set(rule.id, []);
  }

  const context: Record<string, any> = {
    ...buildBatchContext(tagIdToObjects),
    ...buildTableTotalsBatchContext(validTableTotalsRules),
  };

  return { scripts, context, expectedScriptIds, earlyResults, scriptsByRuleId, contextKeysByRuleId };
};

const toContextKey = (id: string) => `OBJ_${id.replace(/-/g, "_")}`;

/**
 * Context keys a CONSISTENCY/CUSTOM rule needs: the union (not sum) of its tags' objects'
 * `OBJ_<id>` keys, so two rules referencing the same object share that one key — the union
 * `packRules` relies on when capping a chunk's context-key count.
 */
const buildRuleContextKeys = (ruleTags: Tag[], tagIdToObjects: Map<string, CoObjectSummary[]>): string[] => {
  const keys = new Set<string>();
  for (const tag of ruleTags) {
    for (const obj of tagIdToObjects.get(tag.id) ?? []) {
      if (obj.text != null) keys.add(toContextKey(obj.id));
    }
  }
  return [...keys];
};

function doubleEscapeQuotes(value: string): string {
  return value.replace(/"/g, '\\"');
}

const resolveFormat = (fmt: FormatMetadata | null | undefined): number | string | null => {
  if (!fmt?.numberFormatValue) return null;
  if (fmt?.numberType === "Custom") return `"${doubleEscapeQuotes(fmt.numberFormatValue)}"`;
  return builtInNumberFormat.find((f) => f.key === String(fmt.numberFormatValue)) ? fmt.numberFormatValue : null;
};

const evaluateFormat = (objectFormat: FormatMetadata, documentFormat: FormatMetadata): number | string | null => {
  return resolveFormat(objectFormat) ?? resolveFormat(documentFormat);
};

const buildParseExpression = (key: string, object: CoObjectSummary, documentMetadata: DocumentPartMetadata): string => {
  const fmt = evaluateFormat(object?.formatMetadata, documentMetadata?.formatMetadata);
  const locale = object?.formatMetadata?.locale || documentMetadata?.formatMetadata?.locale;

  if (fmt && locale) return `PARSE(${key}, ${fmt}, "${locale}")`;
  if (fmt) return `PARSE(${key}, ${fmt})`;
  return `PARSE(${key})`;
};

const buildConsistencyScriptItem = (
  ruleId: string,
  tag: Tag,
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata
): string => {
  console.debug("[buildConsistencyScriptItem] Validating objects for tag:", tag.id);
  if (tag.isDeleted) {
    throw new MissingTagException(tag.label);
  }

  const objects = tagIdToObjects.get(tag.id);
  if (!objects || objects.length === 0) {
    throw new MissingObjectsException(tag.label);
  }
  if (objects.length === 1) {
    throw new SingleObjectException(tag.label);
  }

  const scriptId = buildScriptId(RuleType.CONSISTENCY, ruleId, tag.id);

  console.debug("[buildConsistencyScriptItem] Building PARSE expressions for", objects.length, "objects");
  const parseExpressions = objects.map((obj) => `${buildParseExpression(toContextKey(obj.id), obj, documentMetadata)}`);

  const equalExpression = `EQUAL(${parseExpressions.join(", ")})`;

  console.debug("[buildConsistencyScriptItem] Building FORMAT expression for failure message");
  const formatTemplate = objects.map((obj, idx) => `${toContextKey(obj.id)}=\${${idx + 1}}`).join("\\n");
  const formatExpression = `FORMAT("${formatTemplate}", ${parseExpressions.join(", ")})`;

  return `@scriptId ${scriptId}\nVALIDATE(${equalExpression},${formatExpression})`;
};

const buildScriptItem = (
  rule: Rule,
  ruleTags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  documentMetadata: DocumentPartMetadata
): string | undefined => {
  if ((rule.script || "").trim()) {
    const tokenizedTag = tokenizeScriptForRendering(rule.script || "").map((token) => {
      if (token.type != "TAG") return token.text;

      const tag = ruleTags.find((t) => t.id === token.tagId);
      if (!tag || tag.isDeleted) {
        const label = tag?.label ?? token.tagId;
        console.debug("Missing tag:", label);
        throw new MissingTagException(label);
      }

      const objects = tagIdToObjects.get(tag.id);
      if (!objects || objects.length === 0) {
        console.debug("Missing objects for tag:", tag.label);
        throw new MissingObjectsException(tag.label);
      }

      let object: CoObjectSummary;
      console.debug("[buildScriptItem] Resolving first object for tag:", token.tagId);
      object = objects[0];
      return buildParseExpression(toContextKey(object.id), object, documentMetadata);
    });

    const scriptId = buildScriptId(RuleType.CUSTOM, rule.id);
    return `@scriptId ${scriptId}\nVALIDATE(${tokenizedTag.join("")})`;
  }
};

// Return type is narrower than the full `RunStatusType` union: RUNNING/QUEUED are UI-only states
// with no backing ScriptResultItem, so this synchronous aggregation never produces them. Mirrors
// `runAggregation.ts`'s aggregate contract for the streaming path — keep the two in sync.
export const evaluateStatus = (results: ScriptResultItem[]): "ERROR" | "FAILED" | "VERIFIED" | "CANCELED" => {
  // EXECUTION_ABORTED is a cancellation, not a failure — checked before the generic ERROR/FAILED
  // rules so an aborted script is never mislabelled ERROR.
  if (results.some((r) => r.exceptionCode === EXECUTION_ABORTED_EXCEPTION_CODE)) return "CANCELED";
  if (results.some((r) => !r.status || r.status.toUpperCase().includes("ERROR"))) return "ERROR";
  if (results.some((r) => r.status !== "SUCCESS")) return "FAILED";
  return "VERIFIED";
};

/**
 * Parses a failureMessage in the format:
 *   OBJ_69c4e2c1749a31508b11ec5a=1\nOBJ_69c4e585749a31508b11ec61=2
 * into a CoObjectSummary[], using the id extracted from the context key
 * (stripping the OBJ_ prefix) and the parsed value as text.
 */
const parseFailureMessage = (failureMessage: string, objects?: CoObjectSummary[]): CoObjectSummary[] => {
  const result: CoObjectSummary[] = [];
  const lines = (failureMessage ?? "").split("\n").filter(Boolean);
  for (const line of lines) {
    const eqIndex = line.indexOf("=");
    if (eqIndex === -1) continue;
    const contextKey = line.slice(0, eqIndex).trim();
    const text = line.slice(eqIndex + 1).trim();
    const id = contextKey.startsWith("OBJ_") ? contextKey.slice(4) : contextKey;
    const found = objects?.find((o) => toContextKey(o.id) === contextKey);
    result.push(found ? { ...found, text } : { id, text });
  }
  return result;
};

const groupConsistencyResultsByTag = (
  consistencyResults: ScriptResultItem[],
  ruleId: string,
  tags: Tag[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  errorKindMap: Map<string, ConsistencyTagErrorKind>
): ConsistencyTagResult[] => {
  const tagPrefix = `${CONSISTENCY_RULE_PREFIX}${ruleId}_`;
  const tagMap = new Map(tags.map((t) => [t.id, t]));

  console.debug("[groupConsistencyResultsByTag] Grouping", consistencyResults.length, "results by tag");
  return consistencyResults.map((result) => {
    const tagId = (result.scriptId ?? "").slice(tagPrefix.length);
    const label = tagMap.get(tagId)?.label ?? tagId;
    const status = evaluateStatus([result]);

    let objects: CoObjectSummary[];
    let failureMessage: string;
    let errorMessage: string;

    switch (status) {
      case "VERIFIED":
        console.debug("[groupConsistencyResultsByTag] VERIFIED - using first object text for tag:", tagId);
        objects = [tagIdToObjects.get(tagId)?.[0]];
        break;
      case "FAILED":
        console.debug("[groupConsistencyResultsByTag] FAILED - parsing failure message for tag:", tagId);
        objects = parseFailureMessage(result.failureMessage ?? "", tagIdToObjects.get(tagId));
        failureMessage = result.failureMessage;
        break;
      case "ERROR":
      default:
        objects = tagIdToObjects.get(tagId);
        errorMessage = result.errorMessage;
        break;
    }

    const errorKind = errorKindMap.get(result.scriptId ?? "");
    return { label, objects, status, errorMessage, failureMessage, errorKind };
  });
};

const buildConsistencyResult = (
  rule: Rule,
  tags: Tag[],
  consistencyResults: ScriptResultItem[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  now: string,
  errorKindMap: Map<string, ConsistencyTagErrorKind>
): RunResult => {
  const consistencyStatus = evaluateStatus(consistencyResults);
  const tagToConsistencyResults = groupConsistencyResultsByTag(
    consistencyResults,
    rule.id,
    tags,
    tagIdToObjects,
    errorKindMap
  );

  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: "CONSISTENCY",
    info: {
      status: consistencyStatus,
      timestamp: now,
      localExpression: rule.script ? renderScriptWithTagPills(rule.script, tags) : renderConsistencyContent(rule, tags),
      consistencyTags: tagToConsistencyResults.map(({ label, status, objects, errorMessage, errorKind }) => ({
        label,
        status,
        objects,
        errorMessage,
        errorKind,
      })),
    },
  };
};

const buildCustomResult = (
  rule: Rule,
  tags: Tag[],
  consistencyResults: ScriptResultItem[],
  customResults: ScriptResultItem[],
  tagIdToObjects: Map<string, CoObjectSummary[]>,
  now: string,
  errorKindMap: Map<string, ConsistencyTagErrorKind>
): RunResult => {
  console.debug("[buildCustomResult] Evaluating consistency status for rule:", rule.id);
  const consistencyStatus = evaluateStatus(consistencyResults);
  if (consistencyStatus !== "VERIFIED") {
    const tagToConsistencyResults = groupConsistencyResultsByTag(
      consistencyResults,
      rule.id,
      tags,
      tagIdToObjects,
      errorKindMap
    );

    return {
      id: `${rule.id}-${now}`,
      ruleId: rule.id,
      title: rule.title,
      ruleType: "CUSTOM",
      info: {
        status: consistencyStatus,
        timestamp: now,
        localExpression: renderScriptWithTagPills(rule.script, tags),
        consistencyTags: tagToConsistencyResults.map(({ label, status, objects, errorMessage, errorKind }) => ({
          label,
          status,
          objects,
          errorMessage,
          errorKind,
        })),
      },
    };
  }

  console.debug("[buildCustomResult] Consistency verified, evaluating custom script for rule:", rule.id);
  const originalScriptContext: Record<string, string> = {};
  for (const tag of tags) {
    const firstText = tagIdToObjects.get(tag.id)?.[0]?.text;
    if (firstText) originalScriptContext[tag.id] = firstText;
  }

  const customStatus = evaluateStatus(customResults);
  const message =
    customStatus === "ERROR" && customResults.some((r) => r.status?.toUpperCase() === "COMPILE_ERROR")
      ? formatMessage("cof.validation.message.failed.ruleconsistencyerror")
      : undefined;

  return {
    id: `${rule.id}-${now}`,
    ruleId: rule.id,
    title: rule.title,
    ruleType: "CUSTOM",
    info: {
      status: customStatus,
      timestamp: now,
      localExpression: renderScriptWithTagPills(rule.script, tags),
      evaluatedExpression: renderScriptWithValue(rule.script, originalScriptContext),
      message,
    },
  };
};

// TABLE_TOTALS pure builders

/**
 * Context key for a table cell in a TABLE_TOTALS script: "CELL_<sanitizedRuleId>_<row>_<col>",
 * namespaced per rule so multiple rules referencing the same cell coordinates don't collide when
 * merged into the shared batch context.
 */
const toCellContextKey = (ruleId: string, row: number, col: number): string =>
  `CELL_${ruleId.replace(/-/g, "_")}_${row}_${col}`;

/**
 * Locale-aware PARSE expression for a table cell. Resolves format/locale from the table's own
 * metadata first, falling back to document-level metadata.
 */
const buildCellParseExpression = (
  contextKey: string,
  tableFormatMetadata: FormatMetadata,
  documentMetadata: DocumentPartMetadata
): string => {
  const fmt = evaluateFormat(tableFormatMetadata, documentMetadata?.formatMetadata);
  const locale = tableFormatMetadata?.locale || documentMetadata?.formatMetadata?.locale;
  if (fmt && locale) return `PARSE(${contextKey}, ${fmt}, "${locale}")`;
  if (fmt) return `PARSE(${contextKey}, ${fmt})`;
  return `PARSE(${contextKey})`;
};

/**
 * Synthesizes the scriptRunner expression for a TABLE_TOTALS rule. Cell values aren't embedded in
 * the script — they're supplied via the batch context (`buildTableTotalsContext`), keyed by the
 * `CELL_<ruleId>_r_c` context keys derived from `spec`.
 *
 * Script shape:
 *   @scriptId TABLE_TOTALS_<ruleId>
 *   VALIDATE(
 *     EQUAL(SUM( PARSE(CELL_<ruleId>_r_c, …), … ), PARSE(CELL_<ruleId>_rs_cs, …)),
 *     FORMAT("sum=${1}\nsubtotal=${2}", SUM(…), PARSE(CELL_<ruleId>_rs_cs, …))
 *   )
 *
 * @param ruleId              - The rule id (becomes TABLE_TOTALS_<ruleId> in @scriptId).
 * @param spec                - TableTotalsSpec: addend refs and subtotal ref.
 * @param documentMetadata    - Document format metadata for locale-aware PARSE expressions.
 * @param tableFormatMetadata - Table's own format metadata (takes precedence over documentMetadata).
 */
export const buildTableTotalsScript = (
  ruleId: string,
  spec: TableTotalsSpec,
  documentMetadata: DocumentPartMetadata,
  tableFormatMetadata?: FormatMetadata
): string => {
  const { addends, subtotal } = spec;

  const addendParseExprs = addends.map((ref) =>
    buildCellParseExpression(toCellContextKey(ruleId, ref.row, ref.column), tableFormatMetadata, documentMetadata)
  );
  const subtotalParseExpr = buildCellParseExpression(
    toCellContextKey(ruleId, subtotal.row, subtotal.column),
    tableFormatMetadata,
    documentMetadata
  );

  const sumExpr = `SUM(${addendParseExprs.join(", ")})`;
  const equalExpr = `EQUAL(${sumExpr}, ${subtotalParseExpr})`;
  const formatExpr = `FORMAT("sum=\${1}\\nsubtotal=\${2}", ${sumExpr}, ${subtotalParseExpr})`;

  const scriptId = buildScriptId(RuleType.TABLE_TOTALS, ruleId);
  return `@scriptId ${scriptId}\nVALIDATE(${equalExpr}, ${formatExpr})`;
};

/**
 * Builds the scriptRunner context for a TABLE_TOTALS batch script: maps each addend and the
 * subtotal cell to its snapshot display text, keyed by `CELL_<ruleId>_<row>_<col>` (namespaced per
 * rule so multiple rules can share the same batch context without colliding).
 *
 * @param ruleId - Must match the ruleId passed to `buildTableTotalsScript` for the same rule.
 * @param spec   - The TABLE_TOTALS spec (addend refs + subtotal ref).
 * @param cells  - Snapshot cells (addend + subtotal) extracted from the shared snapshot grid.
 */
export const buildTableTotalsContext = (
  ruleId: string,
  spec: TableTotalsSpec,
  cells: ReadonlyArray<{ row: number; column: number; value: string }>
): Record<string, string> => {
  const cellMap = new Map(cells.map((c) => [`${c.row}_${c.column}`, c.value]));
  const context: Record<string, string> = {};

  for (const ref of spec.addends) {
    const key = toCellContextKey(ruleId, ref.row, ref.column);
    const value = cellMap.get(`${ref.row}_${ref.column}`);
    if (value !== undefined) context[key] = value;
  }

  const subtotalKey = toCellContextKey(ruleId, spec.subtotal.row, spec.subtotal.column);
  const subtotalValue = cellMap.get(`${spec.subtotal.row}_${spec.subtotal.column}`);
  if (subtotalValue !== undefined) context[subtotalKey] = subtotalValue;

  return context;
};

/**
 * Returns the human-readable additive expression for a TABLE_TOTALS result.
 * Form: "a + b + c = subtotal" using snapshot display texts (no arithmetic).
 * Single addend: "a = subtotal". No subtotal: "a + b + c".
 *
 * @param addends  - Addend cells from the run-time snapshot (in spec order).
 * @param subtotal - Optional subtotal cell from the run-time snapshot.
 */
export const buildTableTotalsDisplayExpression = (
  addends: TableCellSnapshot[],
  subtotal?: TableCellSnapshot
): string => {
  const addendsStr = addends.map((a) => a.value).join(" + ");
  const subtotalStr = subtotal ? ` = ${subtotal.value}` : "";
  return `${addendsStr}${subtotalStr}`;
};
